import { RegisterStructure } from './smart-sync';

export class DeviceData {
    inspections: NewInspection[] = [];
    outstandingActions: NewAction[] = [];
    incidents: NewIncident[] = [];
}

/**
 * Added auditTemplateId in 1.19 to store the current template ID at
 * time of event creation.
 */
export interface NewInspection {
    inspectionDate?: Date;
    nextInspectionDate?: Date;
    modificationDate?: Date;
    isPeriodical?: number;
    isOutstanding?: number;
    reviewScheduleType?: number;
    loggedOutstandingDate?: NewInspectionOutstandingDate;
    property?: {
        id: number;
    };
    id?: number;
    moduleId?: number;
    dateId?: number;
    pendingUpload?: number;
    type: {
        id: number;
    };
    subType: {
        id: number;
    };
    notes: string;
    isCompleted?: number;
    unit_id?: number;
    unit_name?: string;
    actions?: NewAction[];
    attachments?: any[];
    audit?: NewInspectionAudit;
    auditTemplateId?: number;
    inProgressAction?: NewAction;
    registerEvent?: NewRegisterEvent;
    location?: NewLocationData;
    // Stores all the custom event field values.
    customFields?: NewRegisterField[];
}

export class NewInspectionOutstandingDate {
    date_id = -1;
    date_value: Date = new Date();
    inspectionId = -1;
    next_date_id = 0;
    next_date_value: Date = new Date('1970-01-01T01:00:00Z');
}

/**
 * Stores a recorded location taken from a geolocation plugin request.
 */
export class NewLocationData {
  timestamp: number;
  longitude: number;
  latitude: number;
  accuracy: number;
}

/**
 * Defines the structure of a saved audit ready for upload.
 */
export class NewInspectionAudit {
    auditTemplate = {
        id: 0
    };
    auditTemplateId = 0;
    isCompleted = 0;
    hiddenTopics: string;
    answers: NewInspectionAuditAnswer[] = [];
}

/**
 * Added the value and fieldType fields to this object in 1.19.
 * The value field can store the signature string or table rows or an array of multi-select selections.
 */
export interface NewInspectionAuditAnswer {
    comment: string;
    question: {
        id: number;
    };
    selectedAnswer: {
        id: number;
    };
    value: any;
    modificationDate: Date;
    attachments: any[];
    fieldType: string;
}
/**
 * Stores an audit table row. This is stored against the audit on the device but is converted into a
 * JSON string when synced up.
 * The tableVisibleFields is used only store fields that will be displayed in the audits view in a table.
 */
export interface NewInspectionAuditTableRow {
    id: string;
    fields: NewInspectionAuditTableField[];
    tableVisibleFields: NewInspectionAuditTableField[];
}

/**
 * Stores an audit table field answer. The attachments are used for file upload fields.
 * The value is used for text, date and signature fields.
 * This is stored against the audit on the device but is converted into a JSON string when synced up.
 */
export interface NewInspectionAuditTableField {
    id: number;
    fieldType: string;
    attachments: any[];
    value: any;
    showInTable: number,
    sortOrder: number
    label?: string
}

/** Stores a register event against an inspection. */
export class NewRegisterEvent {
  isCompleted: number;
  registerId: number;
  newRegisterRows: NewRegisterRow[] = [];
  savedRegisterStructure?: RegisterStructure[];
}

/** Stores a row of data for a register event. */
export interface NewRegisterRow {
  id: number;
  registerId: number;
  isEdited: number;
  timeStamp?: string;
  newRegisterFields: NewRegisterField[];
  newItem?: number;
}

/** Stores a field of data for a register event. */
export interface NewRegisterField {
  keyName: string;
  webName: string;
  value: string;
  id: number;
  fileAttachments: any[];
  fieldType: string;
}

export interface NewAction {
    actionCategory: {
        id: number;
        name?: string;
    };
    actionSubCategory: {
        id: number;
    };
    id?: number;
    actionModuleId?: number;
    moduleId?: number;
    action_details: string;
    action_required: string;
    modificationDate: Date;
    isCompleted: number;
    close_type?: string;
    completionDetails?: string;
    completionAttachments?: any[];
    actioned: number;
    photo?: any;
    inspector: {
        id: number;
    };
    note: string;
    pendingUpload?: number;
    priority: {
        id: number;
        name?: string;
    };
    referenceNo?: string;
    action_date: Date;
    emailDetails?: {
        content: string;
        recipients: NewActionEmailsRecipients[];
    };
    progress?: NewActionProgress[];
    propertyId?: number;
    attachments?: any[];
    registerItemId?: any[];
    registerCreationType?: number;
    auditQuestionId?: number;
    arrayIndex?: number;
}

export interface NewActionEmailsRecipients {
    email: string;
    id: number;
    name: string;
}

export interface NewActionProgress {
    id: number;
    local: number;
    log_date: Date;
    log_entry: string;
    progressCategory: {
        id: number;
    };
    attachment?: any;
}

export interface NewIncident {
    actions: any[];
    formId: number;
    id: number;
    inProgress: number;
    incidentDate: Date;
    incidentTypeName: string;
    isComplete: number;
    pendingUpload: number;
    property: {
        id: number;
    };
    savedIncidentAnswers: NewIncidentAnswer[];
    inProgressAction?: NewAction;
}

export interface NewIncidentAnswer {
    answerDateWebStamp?: string;
    fieldTypeId: number;
    fileAttachments: any[];
    floorplanPositions: NewIncidentFloorplan[];
    markerPositions: NewIncidentPosition[];
    multiSelectAnswers: any[];
    questionAnswer?: string;
    questionAnswerDate?: Date;
    questionId: number;
    selectOptionId: number;
    selectOptionName?: string;
}

export interface NewIncidentPosition {
    xPercentage: number;
    yPercentage: number;
}
export interface NewIncidentFloorplan {
    fileId: number;
    floorplanPositions: NewIncidentPosition[];
}
